import {TokenInput, TokenInputProps} from '@/components/app/inputs';
import {Form, FormProps} from '../../../app/components/Form';
import {Icon} from '../../../app/components/Icon';
import {useCreateBiotwinShareMutation, useGetBiotwinSharesQuery} from '../../../app/services/biotwinShareApi';
import {emailRegExp} from '../../../app/utils/miscUtils';
import {toastError} from '../../../app/toast';
import {useSession} from '../../auth/hooks/useSession';
import {BiotwinSharePrivilege} from '../types';
import {BiotwinShareInfo} from './BiotwinShareInfo';
interface FieldValues {
  emails: string[];
  message: string;
  ownerDisplayName: string;
  privilege: BiotwinSharePrivilege;
}
export interface BiotwinShareFormProps extends Omit<FormProps<FieldValues>, 'fields'> {
  biotwinId: number;
  onSubmitSuccess?: () => void;
}
export const BiotwinShareForm = ({biotwinId, onSubmitSuccess, ...otherProps}: BiotwinShareFormProps) => {
  const session = useSession();
  const {data: biotwinShares = []} = useGetBiotwinSharesQuery();
  const [createBiotwinShare] = useCreateBiotwinShareMutation();
  const existingBiotwinShares = biotwinShares.filter((biotwinShare) => biotwinShare.biotypeId === biotwinId);
  return (
    <Form<FieldValues>
      defaultValues={{
        emails: [],
        message: '',
        privilege: BiotwinSharePrivilege.Read,
        ownerDisplayName: '',
      }}
      onSubmit={(fieldValues) => {
        return Promise.all(
          fieldValues.emails.map((email) =>
            createBiotwinShare({
              userId: biotwinId,
              biotwinShare: {
                ...fieldValues,
                email,
              },
            }).unwrap(),
          ),
        ).then(() => onSubmitSuccess?.());
      }}
      render={({renderError, renderFieldByName, renderSubmitButton}) => {
        return (
          <>
            {existingBiotwinShares.length > 0 && (
              <div className={'flex flex-col gap-4 bg-zinc-200 -mx-6 p-6'}>
                {existingBiotwinShares.map((biotwinShare) => (
                  <BiotwinShareInfo
                    biotwinShare={biotwinShare}
                    key={biotwinShare.id}
                    noAvatar
                    noBiotwinName
                    noMessage
                    readOnly
                  />
                ))}
              </div>
            )}
            {renderError()}
            {/*{renderFieldByName('privilege')}*/}
            {renderFieldByName('emails')}
            <div className={'relative'}>
              <span
                className={'absolute left-0 top-0 text-sm text-blue-400'}
                style={{
                  marginLeft: '6.75rem',
                  marginTop: '0.125rem',
                }}
              >
                <Icon className={'text-sm'} icon={'info-circle'} /> The name that will appear to the recipient.
              </span>
              {renderFieldByName('ownerDisplayName')}
            </div>
            {renderFieldByName('message')}
            {renderSubmitButton()}
          </>
        );
      }}
      submitButton={{
        children: 'Share',
      }}
      {...otherProps}
      fields={({setValue}) => [
        {
          name: 'privilege',
          label: 'Privilege',
          rules: {
            required: true,
          },
          type: 'dropdown',
          typeOptions: {
            itemTemplate: (option) => {
              return (
                <div className="flex gap-2 items-center">
                  <Icon icon={option.icon} />
                  <span>{option.label}</span>
                </div>
              );
            },
            valueTemplate: (option) => {
              return (
                <div className="flex gap-2 items-center">
                  <Icon icon={option.icon} />
                  <span>{option.label}</span>
                </div>
              );
            },
            options: [
              {
                icon: 'eye',
                label: 'Can Read Only',
                value: BiotwinSharePrivilege.Read,
              },
              {
                icon: 'pencil',
                label: 'Can Read & Write',
                value: BiotwinSharePrivilege.Write,
              },
            ],
            optionLabel: 'label',
            optionValue: 'value',
          },
        },
        {
          name: 'emails',
          label: 'Email',
          rules: {},
          type: TokenInput,
          typeOptions: {
            addOnBlur: true,
            onValueChange: (e) => {
              if (e == null) {
                setValue('emails', []);
              } else if (e.some((email) => !emailRegExp.test(email))) {
                toastError('Invalid email address.');
              } else {
                setValue('emails', e ?? []);
              }
            },
            className: 'w-full',
          } as TokenInputProps,
        },
        {
          name: 'ownerDisplayName',
          label: 'Your Name',
          rules: {
            maxLength: 50,
          },
          type: 'text',
          typeOptions: {
            maxLength: 50,
            placeholder: session?.name,
          },
        },
        {
          name: 'message',
          label: 'Message',
          rules: {},
          type: 'textarea',
          typeOptions: {
            rows: 4,
          },
        },
      ]}
    />
  );
};
